import type { DataType, OperatorId, ValueKind } from '@resource-kit/model';

export type OperatorSpec = {
  id: OperatorId;
  label: string;
  valueKind: ValueKind;
};

const op = (id: OperatorId, label: string, valueKind: ValueKind): OperatorSpec => ({
  id,
  label,
  valueKind,
});

export const operatorRegistry: Record<DataType, OperatorSpec[]> = {
  string: [
    op('like', 'op.contains', 'text'),
    op('ilike', 'op.containsCI', 'text'),
    op('eq', 'op.equals', 'text'),
    op('ne', 'op.notEquals', 'text'),
    op('lt', 'op.lessThanLex', 'text'),
    op('gt', 'op.greaterThanLex', 'text'),
  ],
  number: [
    op('eq', 'op.equals', 'number'),
    op('ne', 'op.notEquals', 'number'),
    op('lt', 'op.lessThan', 'number'),
    op('gt', 'op.greaterThan', 'number'),
    // Range comparison on a numeric column (>=, <=, between), mirroring the
    // date registry. Correct only for columns with numeric SQLite affinity
    // (INTEGER / REAL) — decimal-string money columns keep string affinity
    // and are intentionally NOT whitelisted for these (see the resource
    // whitelists, #filterable-coverage).
    op('from', 'op.from', 'number'),
    op('to', 'op.to', 'number'),
    op('between', 'op.between', 'numberRange'),
  ],
  boolean: [op('eq', 'op.is', 'enum_single')],
  enum: [
    op('eq', 'op.is', 'enum_single'),
    op('ne', 'op.isNot', 'enum_single'),
    op('in', 'op.in', 'enum_multi'),
  ],
  date: [
    op('from', 'op.from', 'date'),
    op('to', 'op.to', 'date'),
    op('between', 'op.between', 'dateRange'),
    op('eq', 'op.equalsDay', 'date'),
  ],
  // An INSTANT column deliberately offers NO `eq`, while the `date` list
  // above still does (#2106). The asymmetry is the point — do not "restore"
  // it for symmetry's sake:
  //
  //   - `date` equality names a real bucket. `eq:2026-08-13` on a day column
  //     means that whole calendar day, and the backend `dateText` fields
  //     (order.delivery_date, product/job.shipping_date) whitelist `eq` today.
  //   - `datetime` equality would have to match a single millisecond. NOT ONE
  //     of the instant fields declared across the kernel-parsed route configs
  //     admits `eq`: every one whitelists exactly ["from","to","between"], so
  //     on those resources the op was already intersected away and unreachable.
  //     (Deliberately stated without counts — a tally here would go stale the
  //     next time a route is added, and the claim does not depend on one.)
  //   - Where no whitelist exists the op WAS reachable, and the UI still could
  //     not express it: the spec declared ValueKind 'date', so QueryBuilder
  //     rendered a day DatePicker whose value can never equal a stored instant.
  //
  // So this deletes only capability that was broken or dead. A hand-edited URL
  // bypasses this registry entirely, which is why `serialize.ts` still
  // completes an `eq` bound on a datetime column — that path is unaffected.
  datetime: [
    op('from', 'op.from', 'date'),
    op('to', 'op.to', 'date'),
    op('between', 'op.between', 'dateRange'),
  ],
  id: [op('eq', 'op.equals', 'text'), op('in', 'op.in', 'text')],
  json: [
    op('like', 'op.contains', 'text'),
    op('ilike', 'op.containsCI', 'text'),
    op('eq', 'op.equals', 'text'),
  ],
};

/**
 * The ops offered for a column. `overrides` is the column's backend
 * `filterable` whitelist (mirrored onto `ColumnSchema.operations`): when
 * present the registry list is trimmed to it, in registry order, so the
 * QueryBuilder never offers an op the backend would silently drop
 * (#contains-filter). An intersection also drops any drifted override the
 * type does not support. Absent overrides ⇒ the full registry (columns
 * with no declared whitelist keep the prior behavior).
 */
export function getOperatorsFor(type: DataType, overrides?: readonly OperatorId[]): OperatorSpec[] {
  const base = operatorRegistry[type] || [];
  if (overrides === undefined) return base;
  const allowed = new Set(overrides);
  return base.filter((o) => allowed.has(o.id));
}

/**
 * Whether a draft condition's value is complete/parseable for the op's
 * value kind — the single validation the QueryBuilder runs before a draft
 * becomes a chip. Pure so it is unit-testable; notably `number` requires a
 * non-blank input (`Number('') === 0`, so a bare `Number.isFinite` check
 * would let an EMPTY value through and mint a chip filtering on nothing).
 */
export function isConditionValueValid(kind: ValueKind, value: unknown): boolean {
  if (kind === 'none') return true;
  if (kind === 'dateRange') {
    const [a, b] = Array.isArray(value) ? (value as [string, string]) : ['', ''];
    return Boolean(a && b) && !Number.isNaN(Date.parse(a)) && !Number.isNaN(Date.parse(b));
  }
  if (kind === 'numberRange') {
    const [a, b] = Array.isArray(value) ? (value as [string, string]) : ['', ''];
    return a !== '' && b !== '' && Number.isFinite(Number(a)) && Number.isFinite(Number(b));
  }
  const v = Array.isArray(value) ? value.join(',') : String(value ?? '');
  if (kind === 'number') {
    return v.trim() !== '' && Number.isFinite(Number(v));
  }
  if (kind === 'date') {
    return v.trim().length > 0 && !Number.isNaN(Date.parse(v));
  }
  return v.trim().length > 0;
}
